import { useCallback, useState } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { colors } from "theme/palette";
import { useRedirectUponFetchFailure } from "utils/common";

import Button from "components/Button";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";

import { ECompanySettingsFeatureType } from "entities/Company/components/CompanySettings/constants";
import { useCheckFeature } from "entities/Company/components/CompanySettings/utils";
import { useGuessedConnectionWithoutCreatedValueCount } from "entities/Deal/sdk";
import { useChecklistFileImportReady } from "entities/TableStorage/hooks";

import CategorizationDialog from "./BulkUploadButton/components/CategorizationDialog";
import FilesUploadDialog from "./BulkUploadButton/components/FilesUploadDialog";
import { DialogOpenStatus } from "./constants";

export interface IAIOnboardingButtons {
  handlePackageUpdate: ({ message }: { message?: string }) => void;
  enableGuessedConnectionCountFetching: boolean;
}

const AIOnboardingButtons = ({
  handlePackageUpdate,
  enableGuessedConnectionCountFetching,
}: IAIOnboardingButtons) => {
  const [checklistFileImportId, setChecklistFileImportId] = useState<
    number | null
  >(null);
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([]);
  const [dialogOpen, setDialogOpen] = useState<DialogOpenStatus>(
    DialogOpenStatus.CLOSED
  );
  const [isCategorizing, setIsCategorizing] = useState<boolean>(false);

  const params = useParams<{
    dealId: string;
  }>();
  const dealId = Number(params.dealId);

  const {
    data: guessedConnectionWithoutCreatedValueCount,
    mutate: refetchGuessedConnectionWithoutCreatedValueCount,
    error: guessedConnectionWithoutCreatedValueCountFetchError,
    isLoading: isFetchingGuessedConnectionWithoutCreatedValueCount,
  } = useGuessedConnectionWithoutCreatedValueCount({
    dealId,
    enabled: enableGuessedConnectionCountFetching,
  });

  useRedirectUponFetchFailure({
    error: guessedConnectionWithoutCreatedValueCountFetchError,
    loading: isFetchingGuessedConnectionWithoutCreatedValueCount,
  });

  const onFilesUploadDialogClick = useCheckFeature({
    feature: ECompanySettingsFeatureType.ONBOARDING_COMPANY_PACKAGE,
    callback: () => {
      setSelectedFiles([]);
      setDialogOpen(DialogOpenStatus.FILE_UPLOAD);
    },
  });
  const onCategorizeDocumentsClick = useCheckFeature({
    feature: ECompanySettingsFeatureType.ONBOARDING_COMPANY_PACKAGE,
    callback: () => {
      setDialogOpen(DialogOpenStatus.DETAIL);
    },
  });

  const onFilesUploadSubmit = useCallback(() => {
    setDialogOpen(DialogOpenStatus.DETAIL);
  }, []);
  const onDialogClose = useCallback(() => {
    setDialogOpen(DialogOpenStatus.CLOSED);
    setChecklistFileImportId(null);
    refetchGuessedConnectionWithoutCreatedValueCount();
    setIsCategorizing(false);
  }, [refetchGuessedConnectionWithoutCreatedValueCount]);

  const handleFilesUpload = useCallback(
    ({
      checklistFileImportId,
      categorize,
    }: {
      checklistFileImportId: number;
      categorize: boolean;
    }) => {
      if (categorize) {
        setChecklistFileImportId(checklistFileImportId);
        setIsCategorizing(true);
        onFilesUploadSubmit();
      } else {
        toast.successMessage("The documents have been uploaded successfully!");
        onDialogClose();
      }
    },
    [setChecklistFileImportId, onFilesUploadSubmit, onDialogClose]
  );

  const handleFilesSelect = useCallback(
    ({ files }: { files: Array<File> }) => {
      setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
    },
    [setSelectedFiles]
  );

  const clearFile = useCallback(
    ({ fileToRemove }: { fileToRemove: File }) => {
      setSelectedFiles((prevFiles) =>
        prevFiles.filter((file) => file !== fileToRemove)
      );
    },
    [setSelectedFiles]
  );

  useChecklistFileImportReady({
    successEventHandler: ({
      company_package_file_import_id: incomingChecklistFileImportId,
    }: {
      company_package_file_import_id: number;
    }) => {
      refetchGuessedConnectionWithoutCreatedValueCount();

      if (checklistFileImportId === incomingChecklistFileImportId) {
        setIsCategorizing(false);
      }
    },
  });

  return [
    <>
      <Button
        onClick={onFilesUploadDialogClick}
        variant="outlined"
        sx={{ color: colors.black, whiteSpace: "nowrap" }}
      >
        Bulk upload documents
      </Button>
      <FilesUploadDialog
        dealId={dealId}
        selectedFiles={selectedFiles}
        open={dialogOpen === DialogOpenStatus.FILE_UPLOAD}
        handleFilesUpload={handleFilesUpload}
        handleFilesSelect={handleFilesSelect}
        clearFile={clearFile}
        onClose={onDialogClose}
      />
      <CategorizationDialog
        onClose={onDialogClose}
        isCategorizing={isCategorizing}
        checklistFileImportId={checklistFileImportId}
        open={dialogOpen === DialogOpenStatus.DETAIL}
        handlePackageUpdate={handlePackageUpdate}
      />
    </>,
    !_.isNil(guessedConnectionWithoutCreatedValueCount) &&
      guessedConnectionWithoutCreatedValueCount > 0 && (
        <Stack gap={1} direction="row">
          <Text color={colors.blue100} fontWeight="700">
            {guessedConnectionWithoutCreatedValueCount} uncategorized document
            {guessedConnectionWithoutCreatedValueCount === 1 ? "" : "s"} stored
            in system...
          </Text>
          <Stack alignSelf="flex-start">
            <Button
              onClick={onCategorizeDocumentsClick}
              variant="outlined"
              size="small"
            >
              Categorize documents
            </Button>
          </Stack>
          <CategorizationDialog
            onClose={onDialogClose}
            isCategorizing={isCategorizing}
            checklistFileImportId={checklistFileImportId}
            open={dialogOpen === DialogOpenStatus.DETAIL}
            handlePackageUpdate={handlePackageUpdate}
          />
        </Stack>
      ),
  ];
};

export default AIOnboardingButtons;
